import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import React from "react";
import { useForm } from "react-hook-form";

import { useToast } from "@/components/ui/use-toast";
import { useI18n } from "@/context";

import { reportsApi } from "./reports.api";
import { IUseReports } from "./reports.hooks.interface";
import {
  mapSaleDetailsToSale,
  mapSaleListItemToTSale,
} from "./reports.mappers";
import { reportsFiltersSchema, TReportsFiltersFormData } from "./reports.schema";
import { TSale, TSaleReportFilters, TSaleStatus } from "./reports.types";
import { buildGeneralReportFilters } from "./reports-filter.utils";

export const useReports = (queryEnabled = true): IUseReports => {
  const { t } = useI18n();
  const { toast } = useToast();

  const [currentPage, setCurrentPage] = React.useState(1);
  const [selectedSale, setSelectedSale] = React.useState<TSale | null>(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = React.useState(false);
  const [filters, setFilters] = React.useState<TSaleReportFilters>({});

  const itemsPerPage = 20;

  const filtersForm = useForm<TReportsFiltersFormData>({
    resolver: zodResolver(reportsFiltersSchema),
    defaultValues: {
      status: "",
      partner_name: "",
      client_name: "",
      client_cpf: "",
      sale_id: "",
      start_date: "",
      end_date: "",
      minimum_price: "",
      maximum_price: "",
    },
  });

  const {
    data: salesReport,
    isLoading,
    isFetching,
    isError,
    error,
    isSuccess,
  } = useQuery({
    queryKey: ["salesReport", filters, currentPage],
    queryFn: () =>
      reportsApi.getSalesReport({
        ...filters,
        page: currentPage,
        limit: itemsPerPage,
      }),
    enabled: queryEnabled && currentPage > 0 && itemsPerPage > 0,
  });

  const getSaleDetailsMutation = useMutation({
    mutationFn: (transactionSearchId: string) =>
      reportsApi.getSaleDetails(transactionSearchId),
    onSuccess: (data) => {
      setSelectedSale(mapSaleDetailsToSale(data));
      setIsDetailsModalOpen(true);
    },
    onError: (err: Error) => {
      toast({
        title: t("generic.error"),
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const statusOptions = React.useMemo(
    () => [
      { value: TSaleStatus.pending, label: t("reports.status.pending") },
      { value: TSaleStatus.approved, label: t("reports.status.approved") },
      { value: TSaleStatus.failed, label: t("reports.status.failed") },
      { value: TSaleStatus.refunded, label: t("reports.status.refunded") },
    ],
    [t]
  );

  const partnerOptions = React.useMemo(() => {
    if (!salesReport?.data) return [];
    const uniquePartners = Array.from(
      new Set(salesReport.data.map((sale) => sale.partner))
    );
    return uniquePartners.map((partner) => ({
      value: partner,
      label: partner,
    }));
  }, [salesReport?.data]);

  const exportReportMutation = useMutation({
    mutationFn: () => {
      const { page: _page, limit: _limit, ...exportFilters } = filters;
      return reportsApi.exportSalesReport(exportFilters);
    },
    onSuccess: (csvText) => {
      const blob = new Blob([csvText], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `relatorio-vendas-${format(new Date(), "yyyy-MM-dd")}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast({ title: t("reports.export.success") });
    },
    onError: (err: Error) => {
      toast({
        title: t("generic.error"),
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const handleApplyFilters = React.useCallback(
    (data: TReportsFiltersFormData) => {
      setFilters(buildGeneralReportFilters(data));
      setCurrentPage(1);
    },
    []
  );

  const handleClearFilters = React.useCallback(() => {
    filtersForm.reset({
      status: "",
      partner_name: "",
      client_name: "",
      client_cpf: "",
      sale_id: "",
      start_date: "",
      end_date: "",
      minimum_price: "",
      maximum_price: "",
    });
    setFilters({});
    setCurrentPage(1);
  }, [filtersForm]);

  const handleExportCsv = React.useCallback(() => {
    exportReportMutation.mutate();
  }, [exportReportMutation]);

  const filteredSales = React.useMemo(() => {
    if (!salesReport?.data) return [];
    return salesReport.data.map(mapSaleListItemToTSale);
  }, [salesReport?.data]);

  const handlePageChange = React.useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handleOpenDetails = React.useCallback(
    (sale: TSale) => {
      getSaleDetailsMutation.mutate(sale.id);
    },
    [getSaleDetailsMutation]
  );

  const handleCloseDetails = React.useCallback(() => {
    setIsDetailsModalOpen(false);
    setSelectedSale(null);
  }, []);

  React.useEffect(() => {
    if (error) {
      toast({
        title: t("generic.error"),
        description: error.message,
        variant: "destructive",
      });
    }
  }, [error, toast, t]);

  return {
    sales: filteredSales,
    filteredSales,
    paginatedSales: filteredSales,
    currentPage,
    totalPages: salesReport?.metadata?.total_pages || 1,
    totalItems: salesReport?.metadata?.total || 0,
    itemsPerPage,
    filtersForm,
    handleApplyFilters,
    handleClearFilters,
    handlePageChange,
    statusOptions,
    partnerOptions,
    handleExportCsv,
    isExporting: exportReportMutation.isPending,
    selectedSale,
    isDetailsModalOpen,
    handleOpenDetails,
    handleCloseDetails,
    isLoading,
    isFetching,
    isError,
    error: (error ?? null) as Error | null,
    isSuccess,
  };
};
